<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-table-filter :filter-title="員工打卡記錄" :collapsed="true">
    <oxd-form @submit-valid="onClickView">
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <employee-autocomplete
              v-model="filters.employee"
              :rules="rules.employee"
              :params="{
                includeEmployees: 'currentAndPast',
              }"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.locationId"
              type="select"
              :label="$t('general.location')"
              :options="locations"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <!-- 加入 dropdown 選項 -->
            <oxd-input-field
              v-model="filters.dateRangeOption"
              type="select"
              label="日期範圍"
              :rules="rules.selected"
              :options="dateRangeOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.fromDate"
              :placeholder="$t('general.from')"
              :rules="rules.fromDate"
              label="由日期"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.toDate"
              label="至日期"
              :placeholder="$t('general.to')"
              :rules="rules.toDate"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-divider />

      <oxd-form-actions>
        <oxd-button
          display-type="secondary"
          label="匯出更表記錄(PDF)"
          type="button"
          @click="onExportTimesheet"
        />
        &nbsp;&nbsp;
        <oxd-button
          display-type="secondary"
          label="匯出打卡記錄(Excel)"
          type="button"
          class="desktop-only"
          @click="onExport"
        />
        &nbsp;&nbsp;
        <oxd-button
          display-type="secondary"
          :label="$t('general.view')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="orangehrm-paper-container">
    <table-header
      :total="total"
      :loading="isLoading"
      :show-divider="isEditable"
      :selected="checkedItems.length"
      @delete="onClickDeleteSelected"
    ></table-header>
    <div class="orangehrm-container">
      <oxd-card-table
        v-model:selected="checkedItems"
        :headers="headers"
        :items="items?.data"
        :selectable="isEditable"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {
  required,
  validSelection,
  validDateFormat,
} from '@/core/util/validation/rules';
import {
  freshDate,
  parseDate,
  parseTime,
  formatTime,
  formatDate,
  getStandardTimezone,
} from '@/core/util/helper/datefns';
import {navigate} from '@/core/util/helper/navigation';
import {yearRange} from '@/core/util/helper/year-range';
import useLocale from '@/core/util/composable/useLocale';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import useDateFormat from '@/core/util/composable/useDateFormat';
import RecordCell from '@/orangehrmAttendancePlugin/components/RecordCell.vue';
import RecordLink from '@/orangehrmAttendancePlugin/components/RecordLink.vue';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  props: {
    date: {
      type: String,
      default: null,
    },
    employee: {
      type: Object,
      required: true,
      default: null,
    },
    locations: {
      type: Array,
      default: null,
    },
    accesstoken: {
      type: String,
      default: null,
    },
    empnumber: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    console.log('props', props);
    // 用嚟格式化日期成 "yyyy-mm-dd" 格式（獨立於全局 formatDate）
    const simpleFormatDate = (date) => {
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    };

    // 動態生成最近 3 個月選項，每個選項包含該月第一日同最後一日
    const monthOptions = computed(() => {
      const options = [];
      const today = new Date();
      for (let i = 0; i < 3; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const year = d.getFullYear();
        const month = ('0' + (d.getMonth() + 1)).slice(-2);
        const label = `${year}年${month}月`;
        const fromDate = simpleFormatDate(new Date(year, d.getMonth(), 1));
        const toDate = simpleFormatDate(new Date(year, d.getMonth() + 1, 0));
        options.push({
          id: `month-${i}`,
          label,
          fromDate,
          toDate,
        });
      }
      return options;
    });

    // 動態生成最近 4 個完整星期選項（以星期一為起始，每個選項包含該星期一同星期日）
    const weekOptions = computed(() => {
      const options = [];
      const today = new Date();
      const currentDay = new Date(today);
      let day = currentDay.getDay();
      day = day === 0 ? 7 : day; // 若星期日，視為第7日
      const currentMonday = new Date(currentDay);
      currentMonday.setDate(currentDay.getDate() - (day - 1));
      for (let i = 1; i <= 4; i++) {
        const monday = new Date(currentMonday);
        monday.setDate(currentMonday.getDate() - 7 * i);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        const label = `最近${i}星期 (${simpleFormatDate(
          monday,
        )} ~ ${simpleFormatDate(sunday)})`;
        options.push({
          id: `week-${i}`,
          label,
          fromDate: simpleFormatDate(monday),
          toDate: simpleFormatDate(sunday),
        });
      }
      return options;
    });

    // 將月選項同週選項合併
    const dateRangeOptions = computed(() => {
      return [...weekOptions.value, ...monthOptions.value];
    });

    const filters = ref({
      fromDate: props.date ? props.date : formatDate(freshDate(), 'yyyy-MM-dd'),
      toDate: props.date ? props.date : formatDate(freshDate(), 'yyyy-MM-dd'),
      employee: null,
      locationId: null,
      dateRangeOption: null,
    });

    const serializedFilters = computed(() => {
      return {
        fromDate: filters.value.fromDate,
        toDate: filters.value.toDate,
        location: filters.value.locationId?.id,
        empNumber: filters.value.employee?.id,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/attendance/employees/0/records`,
    );
    const {locale} = useLocale();
    const {jsDateFormat, userDateFormat, timeFormat, jsTimeFormat} =
      useDateFormat();

    const rules = {
      date: [required, validDateFormat(userDateFormat)],
      employee: [validSelection],
      selected: [
        (value) => {
          const selected = dateRangeOptions.value.find(
            (option) => option.id === value.id,
          );
          if (selected) {
            filters.value.fromDate = selected.fromDate;
            filters.value.toDate = selected.toDate;
          }
          console.log('selected', value);
          return true;
        },
      ],
    };

    const attendanceRecordNormalizer = (data) => {
      return data.map((item) => {
        const {punchIn, punchOut} = item;
        const punchInDate = formatDate(
          parseDate(punchIn?.userDate),
          jsDateFormat,
          {locale},
        );
        const punchInTime = formatTime(
          parseTime(punchIn?.userTime, timeFormat),
          jsTimeFormat,
        );
        const punchOutDate = formatDate(
          parseDate(punchOut?.userDate),
          jsDateFormat,
          {locale},
        );
        const punchOutTime = formatTime(
          parseTime(punchOut?.userTime, timeFormat),
          jsTimeFormat,
        );

        return {
          id: item.id,
          emp_middle_name: item.emp_middle_name,
          punch_in_location_name: item.punch_in_location_name,
          punch_out_location_name: item.punch_out_location_name,
          punch_in_address: {
            address: item.punch_in_address,
            latitude: item.punch_in_latitude,
            longitude: item.punch_in_longitude,
            alert: item.punch_in_alert,
          },
          punch_out_address: {
            address: item.punch_out_address,
            latitude: item.punch_out_latitude,
            longitude: item.punch_out_longitude,
            alert: item.punch_out_alert,
          },
          punchIn: {
            ...punchIn,
            userTime: punchInTime,
            userDate: punchInDate,
          },
          punchOut: {
            ...punchOut,
            userTime: punchOutTime,
            userDate: punchOutDate,
          },
          duration: item.duration,
          punchInNote: punchIn.note,
          punchOutNote: punchOut.note,
        };
      });
    };

    const {
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: attendanceRecordNormalizer,
      prefetch: true,
    });

    const totalDuration = computed(() => {
      const meta = response.value?.meta;
      return meta ? meta.sum.label : '0.00';
    });

    return {
      http,
      rules,
      total,
      pages,
      filters,
      pageSize,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
      items: response,
      totalDuration,
      dateRangeOptions,
    };
  },

  data() {
    return {
      yearArray: [...yearRange()],
      headers: [
        {
          name: 'emp_middle_name',
          slot: 'title',
          title: '員工',
          style: {flex: 1},
        },
        {
          name: 'punch_in_location_name',
          slot: 'title',
          title: '進店舖',
          style: {flex: 1},
        },
        {
          name: 'punch_in_address',
          slot: 'title',
          title: '進地點',
          style: {flex: 1},
          cellRenderer: this.linkRenderer,
        },
        {
          name: 'punchIn',
          slot: 'title',
          title: this.$t('attendance.punch_in'),
          style: {flex: 1},
          cellRenderer: this.cellRenderer,
        },
        {
          name: 'punch_out_location_name',
          slot: 'title',
          title: '出店舖',
          style: {flex: 1},
        },
        {
          name: 'punch_out_address',
          slot: 'title',
          title: '出地點',
          style: {flex: 1},
          cellRenderer: this.linkRenderer,
        },
        {
          name: 'punchOut',
          slot: 'title',
          title: this.$t('attendance.punch_out'),
          style: {flex: 1},
          cellRenderer: this.cellRenderer,
        },
      ],
      checkedItems: [],
    };
  },

  methods: {
    cellRenderer(...args) {
      const cellData = args[1];
      return {
        component: RecordCell,
        props: {
          date: cellData.userDate,
          time: cellData.userTime,
          offset: getStandardTimezone(cellData.offset),
        },
      };
    },
    linkRenderer(...args) {
      const cellData = args[1];
      console.log('cellData', cellData);
      return {
        component: RecordLink,
        props: {
          address: cellData.address,
          latitude: cellData.latitude,
          longitude: cellData.longitude,
          alert: cellData.alert ? 'class-alert' : '',
          offset: getStandardTimezone(cellData.offset),
        },
      };
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    onClickView() {
      this.resetDataTable();
    },
    onExport() {
      const baseUrl = `${window.appGlobal.baseUrl}/attendance/exportAttendanceRecord`;
      const queryParameters = new URLSearchParams({
        employeeId: this.filters.employee?.id || '',
        locationId: this.filters.locationId?.id || '',
        fromDate: this.filters.fromDate || '',
        toDate: this.filters.toDate || '',
      }).toString();
      const downUrl = `${baseUrl}?${queryParameters}`;
      window.open(downUrl, '_blank');
    },
    onExportTimesheet() {
      const baseUrl = `${window.appGlobal.baseUrl}/../api/ExportTimesheet.php`;
      const queryParameters = new URLSearchParams({
        employeeId: this.filters.employee?.id || '',
        locationId: this.filters.locationId?.id || '',
        fromDate: this.filters.fromDate || '',
        toDate: this.filters.toDate || '',
      }).toString();
      const downUrl = `${baseUrl}?${queryParameters}`;
      window.open(downUrl, '_blank');
    },
  },
};
</script>

<style scoped>
.desktop-only {
  display: block;
}

/* 喺細螢幕（手機/平板）隱藏 */
@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }
}
</style>
